import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';

// react-bootstrap
import Dropdown from 'react-bootstrap/Dropdown';
import Nav from 'react-bootstrap/Nav';

// project-imports
import useConfig from 'hooks/useConfig';
import { setResolvedTheme } from 'components/setResolvedTheme';
import useAuth from '../../auth/useAuth';
import usePermission from '../../hooks/usePermission';
import { handlerDrawerOpen, useGetMenuMaster } from 'api/menu';
import { NotificationBell, ThemeToggle } from './HeaderControls';
import './header.css';

const ROLE_LABELS = {
  super_admin: 'Super Admin',
  merchant_admin: 'Merchant Admin',
  manager: 'Manager',
  staff: 'Staff'
};

const titleCase = (s) =>
  String(s)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());

// Up to two initials from a name: "Julia Munroe" → "JM", "Spice" → "SP".
const initialsOf = (name = '') => {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (!words.length) return '?';
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[words.length - 1][0]).toUpperCase();
};

export default function Header() {
  const { mode } = useConfig();
  const { user, logout } = useAuth();
  const { has } = usePermission();
  const canOpenSettings = has('settings_access') || user?.is_super == 1 || user?.role?.code === 'super_admin';
  const navigate = useNavigate();

  useEffect(() => {
    setResolvedTheme(mode);
  }, [mode]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster?.isDashboardDrawerOpened;

  const userName = user?.user?.name || 'Guest User';
  const roleLabel = ROLE_LABELS[user?.user?.user_type] || (user?.user?.user_type ? titleCase(user.user.user_type) : '');

  return (
    <header className="pc-header yz-hdr">
      <div className="header-wrapper">
        {/* LEFT — the sidebar toggle, on phones only. Desktop shrink/expand
            lives on the sidebar's own button (SidebarUserCard). */}
        <div className="me-auto pc-mob-drp">
          <Nav className="list-unstyled">
            <Nav.Item className="pc-h-item pc-sidebar-popup">
              <Nav.Link as={Link} to="#" className="pc-head-link ms-0" id="mobile-collapse" onClick={() => handlerDrawerOpen(!drawerOpen)}>
                <i className="ph ph-list" />
              </Nav.Link>
            </Nav.Item>
          </Nav>
        </div>

        {/* RIGHT — notifications, theme, profile */}
        <div className="ms-auto yz-hdr-right">
          <NotificationBell />

          <ThemeToggle />

          {/* PROFILE */}
          <Dropdown align="end">
            <Dropdown.Toggle as="button" type="button" className="yz-hdr-profile arrow-none" id="user-profile-dropdown">
              <span className="yz-hdr-avatar">{initialsOf(userName)}</span>
              <span className="yz-hdr-who">
                <span className="yz-hdr-name">{userName}</span>
                {roleLabel && <span className="yz-hdr-role">{roleLabel}</span>}
              </span>
              <i className="ph ph-caret-down yz-hdr-caret" />
            </Dropdown.Toggle>

            <Dropdown.Menu className="yz-hdr-menu yz-hdr-user">
              <div className="yz-hdr-user-card">
                <span className="yz-hdr-avatar yz-hdr-avatar--lg">{initialsOf(userName)}</span>
                <div className="yz-hdr-user-text">
                  <div className="yz-hdr-user-name">{userName}</div>
                  <div className="yz-hdr-user-email">{user?.user?.email || 'no-email'}</div>
                </div>
              </div>

              {/* Every Settings section needs Access Settings; super admin keeps
                  its Profile (see Settings.jsx). */}
              {canOpenSettings && (
                <>
                  <Dropdown.Item as={Link} to="/settings">
                    <i className="ph ph-gear" />
                    Settings
                  </Dropdown.Item>
                  <Dropdown.Divider />
                </>
              )}
              <Dropdown.Item as="button" type="button" className="yz-hdr-logout" onClick={handleLogout}>
                <i className="ph ph-sign-out" />
                Log out
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </div>
    </header>
  );
}
